import { CSSProperties, FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SelectOptionsDropdown } from "@ryde/controls";
import {
  coursesCalendarSlotIdsByDateSF,
  DateYMDString,
  selectedCoursesCalendarSlotIdsAtom,
  SelectionEnum,
  setCheckedHandlerCoursesCalendarSlots,
  timezoneAdjustedDateToDayStartISO,
  timezoneAdjustedDateToISO,
  useFormats,
  useTimezone,
  wholeColumnSelectedCoursesCalendarSF,
} from "@ryde/shared";

interface IHeadDate {
  style: CSSProperties;
  columnIndex: number;
  dateKeys: DateYMDString[];
}

export const CoursesCalendarDateHeadCell: FC<IHeadDate> = ({
  style,
  columnIndex,
  dateKeys,
}) => {
  const { i18n } = useTranslation();
  const { formatDayOfWeekTranslated, formatDateNormalized } = useFormats();
  const { timezone } = useTimezone();
  const ymd = dateKeys[columnIndex];
  const dateYmd = String(ymd) as DateYMDString;

  const colSlotIds = useRecoilValue(coursesCalendarSlotIdsByDateSF(dateYmd));
  const setSelectedSlotIds = useSetRecoilState(
    selectedCoursesCalendarSlotIdsAtom,
  );
  const colSelectedState = useRecoilValue(
    wholeColumnSelectedCoursesCalendarSF(dateYmd),
  );

  const setChecked = (value: SelectionEnum): void => {
    setCheckedHandlerCoursesCalendarSlots[value]({
      setter: setSelectedSlotIds,
      slotIds: colSlotIds,
    });
  };

  const { dow, dmy } = useMemo(() => {
    if (!ymd) {
      return { dow: "", dmy: "" };
    }
    const startIso = timezoneAdjustedDateToDayStartISO(ymd, timezone);
    const fullIso = timezoneAdjustedDateToISO(ymd, timezone);
    return {
      dow: formatDayOfWeekTranslated(
        i18n.language,
        { weekday: "short" },
        "upper",
        startIso,
      ),
      dmy: formatDateNormalized(fullIso),
    };
  }, [
    formatDateNormalized,
    formatDayOfWeekTranslated,
    i18n.language,
    timezone,
    ymd,
  ]);

  return (
    <div className="head_cell" style={style}>
      <span className="checkbox">
        <SelectOptionsDropdown
          checked={colSelectedState}
          setChecked={setChecked}
        />
      </span>
      <span className="checkbox_label">{dow}</span>
      <span className="full_date">{dmy}</span>
    </div>
  );
};
